import type {CSSProperties, ForwardedRef, PropsWithChildren, ReactElement, RefObject} from 'react'
import {type SafeHTMLString, SafeHTMLText} from '@github-ui/safe-html'
import type {Text} from '@primer/react'
import {Heading, Link} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {useNextHeaderTag} from '../hooks/use-next-header-tag'
import type {LeadingBadge} from './LeadingBadge'
import type {TrailingBadge} from './TrailingBadge'
import {testIdProps} from '@github-ui/test-id-props'

// Use inline styles as these prefixes are not supported in sx
const commonInlineSx = {
  style: {
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
  } as CSSProperties,
}

type BaseTitleHeaderProps = {
  /**
   * The tooltip to add context to the title
   */
  tooltip?: string
  /*
   * An optional element used to indicate information such as the status of the item. Appears before the title text.
   */
  leadingBadge?: ReactElement<typeof LeadingBadge>
  /*
   * An optional element used to indicate information such as the status of the item. Appears after the title text.
   */
  trailingBadge?: ReactElement<typeof TrailingBadge>
}

type TitleHeadingProps = {
  headingRef?: RefObject<HTMLHeadingElement>
  /*
   * Styling for the header tag of the title
   */
  headerSx?: BetterSystemStyleObject
}

function TitleHeading({headerSx, headingRef, children}: PropsWithChildren<TitleHeadingProps>) {
  const titleTag = useNextHeaderTag('listitem')
  return (
    <Heading as={titleTag} className="markdown-title" sx={headerSx} ref={headingRef}>
      {children}
    </Heading>
  )
}

type HeadingLinkProps = {
  /**
   * A ref to the anchor element
   */
  anchorRef?: ForwardedRef<HTMLAnchorElement>
  /**
   * The link that will be opened when the item is clicked
   */
  href?: string
  /**
   * A click handler to be executed when the element is clicked
   */
  onClick?: React.MouseEventHandler<HTMLElement>
  onMouseEnter?: React.MouseEventHandler<HTMLElement>
  onMouseLeave?: React.MouseEventHandler<HTMLElement>
  /*
   * Styling for the anchor tag of the title, if the title is a link
   */
  anchorSx?: BetterSystemStyleObject
  /*
   * An optional prop to pass additional props for the anchor tag of the title, if the title is a link
   * Can be used to add a target or soft navigation, for example
   */
  linkProps?: React.ComponentPropsWithoutRef<typeof Text>
}

function HeadingLink({anchorSx, linkProps, anchorRef, children, ...args}: PropsWithChildren<HeadingLinkProps>) {
  return (
    <Link
      {...testIdProps('listitem-title-link')}
      {...commonInlineSx}
      sx={anchorSx}
      ref={anchorRef}
      {...args}
      {...linkProps}
    >
      {children}
    </Link>
  )
}

export type MarkdownHeaderProps = BaseTitleHeaderProps &
  TitleHeadingProps &
  HeadingLinkProps & {
    /**
     * The rendered markdown text content of the header, to convey the primary meaning of the list item.
     * When provided, `value` will be used as the title's `title` attribute and `markdownValue` will be used as the title's text content.
     * If `markdownValue` includes links and a `href` is provided, the `markdownValue` will build accessible links.
     */
    markdownValue: SafeHTMLString
  }

export function MarkdownHeader({
  markdownValue,
  anchorSx,
  headerSx,
  headingRef,
  tooltip,
  leadingBadge,
  trailingBadge,
  ...props
}: MarkdownHeaderProps) {
  return (
    <TitleHeading headerSx={headerSx} headingRef={headingRef}>
      {leadingBadge}
      {/* markdownValue will provide a link if necessary */}
      <SafeHTMLText {...commonInlineSx} sx={anchorSx} html={markdownValue} title={tooltip} {...props} />
      {trailingBadge}
    </TitleHeading>
  )
}

export type LinkHeaderProps = BaseTitleHeaderProps &
  TitleHeadingProps &
  HeadingLinkProps & {
    /**
     * The text content of the header, to convey the primary meaning of the list item.
     */
    value: SafeHTMLString | string
  }

export function LinkHeader({
  headerSx,
  headingRef,
  value,
  tooltip,
  leadingBadge,
  trailingBadge,
  ...args
}: LinkHeaderProps) {
  return (
    <TitleHeading headerSx={headerSx} headingRef={headingRef}>
      <HeadingLink {...args}>
        {leadingBadge}
        <SafeHTMLText html={value as SafeHTMLString} title={tooltip} />
        {trailingBadge}
      </HeadingLink>
    </TitleHeading>
  )
}

export type StaticHeaderProps = BaseTitleHeaderProps &
  TitleHeadingProps & {
    /**
     * The text content of the header, to convey the primary meaning of the list item.
     */
    value: SafeHTMLString | string
  }

export function StaticHeader({value, headerSx, headingRef, tooltip, leadingBadge, trailingBadge}: StaticHeaderProps) {
  return (
    <TitleHeading headerSx={headerSx} headingRef={headingRef}>
      {leadingBadge}
      <SafeHTMLText {...commonInlineSx} html={value as SafeHTMLString} title={tooltip} />
      {trailingBadge}
    </TitleHeading>
  )
}

try{ TitleHeading.displayName ||= 'TitleHeading' } catch {}
try{ HeadingLink.displayName ||= 'HeadingLink' } catch {}
try{ MarkdownHeader.displayName ||= 'MarkdownHeader' } catch {}
try{ LinkHeader.displayName ||= 'LinkHeader' } catch {}
try{ StaticHeader.displayName ||= 'StaticHeader' } catch {}